import React, { useContext } from "react";
import { useMediaQuery } from "react-responsive";
import Context from "../ProductsProvider";
import styled from "@emotion/styled";
import CurvedElement from "../../shared/CurvedElement";
import CardTags from "./CardTags";
import CardMeta from "./CardMeta";

//style rules
import { Colors, Typography, Sizes } from "../../../styles";

const Card = () => {
	const { data: { items, language } } = useContext(Context);

	const isDesktopOrLaptop = useMediaQuery({
		query: `(min-width: ${Sizes.screens.largeMenu})`,
	});

	const renderSponsorLogo = (isSponsored) => {
		if (!isSponsored) return null;

		const altText = language === "en" ? "Sponsored by BDC" : "Sponsorisé par BDC";

		return (
			<SponsorContainer>
				<SponsorLogo src={`${mainScript.assetPath}images/svg/logos/bdc.svg`} alt={altText} />
			</SponsorContainer>
		);
	}

	const imagePlaceholder = `${mainScript.assetPath}images/png/graphics/hsg-placeholder.png`;

	const renderImage = (image, placeholder) => {
		if (!image) return placeholder;

		return image;
	}

	const renderCompany = (company, language) => {
		if (!company) return null;

		if (language === "en") return `by ${company}`;
		return `par ${company}`;
	}

	const cardLinkAriaLabel = () => {
		if (language === "en") return "go to the product's web page";
		return "aller à la page web du produit";
	}

	if (!items.length > 0) return null;

	return items.map((product, index) => {
		if (product.id === "cta") {
			return (
				<CtaCard key={index}>
					<CtaText className="h4">{product.text}</CtaText>
					<a className="button" href={product.link} target="_blank" rel="noreferrer">
						{ product.linkText }
					</a>
				</CtaCard>
			)
		}

		return (
			<CardContainer key={product.id}>
				<CardLink href={product.url} aria-label={cardLinkAriaLabel()} target="_blank">
					<div>
						{/* hiding the image and setting it as a background on desktop for the hover transition to correctly work.  */}
						<AspectRatioBox imgSrc={renderImage(product.image, imagePlaceholder)}>
							{
								isDesktopOrLaptop ?
									null :
									<img src={renderImage(product.image, imagePlaceholder)} alt={product.imageAlt} loading="lazy" />
							}
						</AspectRatioBox>
						{renderSponsorLogo(product.sponsored)}
					</div>
					<CardContent>
						<CardTags
							communities={product.communities}
						/>
						<CardTitle dangerouslySetInnerHTML={{ __html: product.title }} target="_blank"></CardTitle>
						<CardSubtitle dangerouslySetInnerHTML={{ __html: `${renderCompany(product.company_name, language)}` }}></CardSubtitle>
						<CardDescription dangerouslySetInnerHTML={{ __html: product.description }}>
						</CardDescription>
					</CardContent>
					<CardMeta
						region={product.regions[0]?.abbreviation}
						price={product.price_text ? product.price_text : product.price}
					/>
					<CurvedElement
						elementType="circle-button"
						alignment="right"
						color={Colors.base.white}
					>
						<Button>
							<svg viewBox="0 0 23 16" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
								<path fillRule="evenodd" clipRule="evenodd" d="M17.4398 7.99779C17.441 7.9512 17.4344 7.90481 17.4205 7.86137C17.4066 7.81793 17.3858 7.77832 17.3591 7.74489C17.3324 7.71146 17.3005 7.68489 17.2652 7.66675C17.23 7.64861 17.1921 7.63928 17.1538 7.6393C15.9171 7.53687 13.7887 7.38323 11.2648 7.32178L8.741 7.32178C7.89972 7.32178 7.05844 7.32178 6.21716 7.39348C4.21492 7.47542 2.19585 7.60857 0.328207 7.72124C0.284403 7.72122 0.24104 7.73188 0.200675 7.75259C0.160309 7.7733 0.123759 7.80365 0.09318 7.84183C0.0626006 7.88002 0.0386111 7.92527 0.0226261 7.97492C0.00664123 8.02458 -0.00101542 8.07762 0.000107744 8.13094C4.753e-05 8.18382 0.00883952 8.23616 0.0259471 8.28477C0.0430546 8.33338 0.0681188 8.37723 0.0996099 8.41366C0.131101 8.45008 0.168358 8.47831 0.209108 8.49662C0.249857 8.51493 0.293244 8.52294 0.336619 8.52015C2.17061 8.52015 4.18968 8.63282 6.22557 8.66355L8.74941 8.66355L11.2733 8.66355C13.7971 8.57137 15.875 8.41773 17.1622 8.387C17.2025 8.38757 17.2424 8.37746 17.2791 8.35736C17.3159 8.33727 17.3486 8.30768 17.375 8.27065C17.4014 8.23361 17.4209 8.19002 17.4321 8.14291C17.4433 8.09579 17.4459 8.04627 17.4398 7.99779Z" fill="white"/>
								<path fillRule="evenodd" clipRule="evenodd" d="M21.8316 6.59462C21.4553 6.10727 21.0131 5.67452 20.5178 5.30876L16.5484 2.03819C16.139 1.69615 15.7096 1.37878 15.2625 1.08777C15.0749 0.965917 14.8572 0.898208 14.6336 0.89209C14.5075 0.89209 14.3866 0.942157 14.2975 1.03128C14.2084 1.1204 14.1583 1.24127 14.1583 1.3673C14.1583 1.77263 14.1583 1.3673 15.7657 2.9327L19.3437 6.59462C19.5115 6.76234 19.777 6.972 20.0146 7.22358C20.2522 7.47516 20.392 7.54504 20.2662 7.55902C20.1753 7.76668 20.0474 7.95611 19.8888 8.11809C19.6093 8.43956 19.2878 8.73307 19.0782 8.94272C18.5201 9.5587 17.9225 10.1376 17.2891 10.6758C16.6714 11.2222 16.0177 11.7265 15.3324 12.1853L13.3617 13.583C13.1359 13.7293 12.9372 13.9138 12.7746 14.1281C12.6867 14.2485 12.648 14.3978 12.6661 14.5458C12.6843 14.6937 12.7581 14.8292 12.8725 14.9248C13.3197 15.372 13.0681 15.0506 16.0452 13.4153C16.8324 13.0042 17.5891 12.5371 18.3094 12.0176C19.0319 11.4904 19.7226 10.921 20.378 10.3124C20.8481 9.88648 21.2785 9.41866 21.6639 8.91477C21.9005 8.60115 22.063 8.23797 22.1391 7.85253C22.1858 7.41024 22.0771 6.96549 21.8316 6.59462Z" fill="white"/>
							</svg>
						</Button>
					</CurvedElement>
				</CardLink>
			</CardContainer>
		);
	});
};

export default Card;

const CtaCard = styled.li`
	display: flex;
	flex-direction: column;
	gap: 4rem;
	align-items: center;
	justify-content: center;
	background-color: ${Colors.fp.pineGreen20};
	border-radius: 3rem;
	padding: 2rem;
	margin-bottom: unset;
	min-height: 37.6rem;

	@media only screen and (min-width: ${Sizes.screens.large}) {
		min-height: unset;
		height: 56.4rem;
	}
`;

const CtaText = styled.h2`
	text-align: center;
`;

const Button = styled.div`
	display: block;
	color: white;
	background-color: ${Colors.fp.pineGreen};
	height: 4.5rem;
	width: 4.5rem;
	border-radius: 50%;
	border: none;
	font-size: 2rem;
	padding: 0;

	svg {
		height: 1.4rem;
		transition: transform .3s;
		position: relative;
		transform: rotate(-45deg);
		top: 22%;
		right: 26%;
	}
`;

const AspectRatioBox = styled.div`
	height: 0;
	width: 100%;
	position: relative;
	padding-top: calc(270 / 363 * 100%);
	border-radius: 3rem 3rem 0 0;
	overflow: hidden;

	@media only screen and (min-width: ${Sizes.screens.large}) {
		background-image: ${props => `url(${props.imgSrc})`};
		padding-top: calc(270 / 289 * 100%);
		background-size: cover;
		background-position: center top;
		background-repeat: no-repeat;
		height: 100%;
	}

	@media only screen and (min-width: calc(${Sizes.screens.large} + 376px)) {
		padding-top: calc(300 / 289 * 100%);
	}

	img {
		position: absolute;
		top: 0;
		left: 0;
		width: 100%;
		height: 100%;
		object-fit: cover;
	}
`;

const SponsorLogo = styled.img`
	height: 100%;
	width: auto;
	position: relative;
	top: 36%;
	left: 50%;
	transform: translate(-50%, -50%);
`;

const SponsorContainer = styled.div`
	padding: 0.3rem;
	position: absolute;
	top: 1.4rem;
	left: 1.4rem;
	height: 2.4rem;
	width: 4.2rem;
	border-radius: 0.6rem;
	overflow: hidden;
	background-color: ${Colors.base.white};
	border: 0.1rem solid ${Colors.fp.pineGreen60};
	transition: opacity 0.5s;
`;

const CardDescription = styled.p`
	margin-top: 1.5rem;
	font-size: 1.4rem;
	font-weight: ${Typography.fontWeight.medium};
	line-height: 1.9rem;

	@media only screen and (min-width: ${Sizes.screens.large}) {
		overflow: hidden;
		width: 100%;
		display: -webkit-box;
		-webkit-line-clamp: 4;
		-webkit-box-orient: vertical;
		max-height: 7.6rem;
		transition: max-height 0.5s;
	}
`;

const CardContainer = styled.li`
	margin-bottom: unset;
`;

const CardLink = styled.a`
	background-color: ${Colors.fp.pineGreen20};
	border-radius: 3rem;
	position: relative;
	padding-bottom: 5rem;
	margin-bottom: unset;
	display: block;
	height: 100%;

	@media only screen and (min-width: ${Sizes.screens.large}) {
		display: grid;
		grid-template-columns: 1fr;
		grid-template-rows: 300px 175px 39px;
		transition: grid-template-rows 0.4s;
		height: unset;
	}

	&:hover,
	&:focus-visible {
		@media only screen and (min-width: ${Sizes.screens.large}) {
			grid-template-rows: 154px 321px 39px;
			transition: grid-template-rows 0.5s;

			${Button} {
				svg {
					transform: scale(1.4) rotate(-35deg);
				}
			}

			${SponsorContainer} {
				opacity: 0;
				transition: opacity 0.5s;
			}

			${CardDescription} {
				display: block;
				max-height: 100%;
				transition: max-height 0.5s;

			}
		}
	}
`;

const CardContent = styled.div`
	padding: 1.4rem 1.4rem 0;
	position: relative;
	background-color: ${Colors.fp.pineGreen20};
`;

const CardSubtitle = styled.p`
	font-size: 1.5rem;
	font-weight: ${Typography.fontWeight.bold};
	line-height: 1.9rem;
	letter-spacing: -0.003rem;
	margin-top: 0.4rem;
`;

const CardTitle = styled.p`
	font-size: 1.7rem;
	font-weight: ${Typography.fontWeight.bold};
	line-height: 2.4rem;
	display: block;
	color: ${Colors.base.black};
	margin-bottom: unset;
`;
